import { useTheme } from "@emotion/react";
import { ClearOutlined } from "@mui/icons-material";
import {
  Button,
  Chip,
  IconButton,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import MenuAcessos from "../MenuPermissionTree";
import useStyles from "./styles";
// Endpoints do lado interno (default). O portal do cliente injeta os seus.
const endpointsInterno = {
  allRoutes: "find/allRoutes",
  create: "create/permissaoUser",
  edit: "/edit/permissaoUser",
};

export default function ModalCreatePermissao({
  open,
  setOpen,
  setConfirmacaoSaidaPagina,
  routesSelect = [],
  setRoutesSelect = () => {},
  permissao = {},
  setPermissao = () => {},
  setAllPermissoes = () => {},
  isEdit = false,
  setSnackbar = () => {},
  permissaoObject = {},
  apiInstance = api,
  endpoints = endpointsInterno,
  iconMap = {},
  permissaoProtegida = "TÉCNICO",
  // Filtra quais rotas podem ser exibidas na árvore de acessos. Usado pelo
  // portal do cliente para esconder telas que aquele cliente não possui.
  filtrarRota = null,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState(false);
  const colors = [
    "colors.green.medium",
    "colors.purple.medium",
    "colors.pink.medium",
    "colors.red.medium",
    "colors.orange.medium",
  ];

  const closeModal = () => {
    setOpen(false);
    setPermissao({ codigoPermissao: "", descricao: "", color: colors[0] });
    setRoutesSelect([]);
  };
  useEffect(() => {
    getAllRoutes();
    if (!isEdit) {
      setPermissao({ ...permissao, color: colors[0] });
    }
  }, []);

  const getAllRoutes = async () => {
    try {
      const response = await apiInstance.get(endpoints.allRoutes);
      setRoutes(response?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  // Árvore exibida: remove recursivamente as rotas que não se aplicam ao contexto
  const routesVisiveis = useMemo(() => {
    if (typeof filtrarRota !== "function") return routes;

    const filtrar = (nodes = []) =>
      nodes
        ?.filter((node) => filtrarRota(node))
        ?.map((node) => ({
          ...node,
          children: filtrar(node?.children),
        }));

    return filtrar(routes);
  }, [routes, filtrarRota]);

  // Ids que já estavam salvos na permissão mas não aparecem na árvore filtrada.
  // Precisam ser preservados no salvamento para não remover acessos sem querer.
  const idsOcultosSelecionados = useMemo(() => {
    if (typeof filtrarRota !== "function") return [];

    const idsVisiveis = new Set();
    const coletar = (nodes = []) =>
      nodes?.forEach((node) => {
        idsVisiveis.add(node?.id);
        coletar(node?.children);
      });
    coletar(routesVisiveis);

    return (routesSelect || [])
      .filter((route) => !idsVisiveis.has(route?.id))
      .map((route) => ({ id: route?.id }));
  }, [routesVisiveis, routesSelect, filtrarRota]);

  // O que efetivamente vai para o backend
  const routesParaSalvar = () => [
    ...(routesSelect || []),
    ...idsOcultosSelecionados.filter(
      (oculta) => !routesSelect?.some((route) => route?.id === oculta?.id),
    ),
  ];

  const createPermissao = async () => {
    if (
      routesSelect?.length === 0 ||
      !permissao?.codigoPermissao ||
      !permissao?.color
    ) {
      if (routesSelect.length === 0 || !routesSelect) {
        setSnackbar({
          children: "Selecione pelo menos uma rota",
          severity: "warning",
        });
      }
      setError(true);
      return;
    }
    setLoading(true);
    try {
      const response = await apiInstance.post(endpoints.create, {
        codigoPermissao: permissao?.codigoPermissao?.toUpperCase(),
        descricao: permissao?.descricao,
        color: permissao?.color,
        routesPermissao: routesParaSalvar(),
      });
      setAllPermissoes(response.data);
      setSnackbar({
        children: "Permissão criada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        children:
          error?.response?.data?.error || "Error: Falha ao criar permissão",
        severity: "error",
      });
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  const editPermissao = async () => {
    setLoading(true);
    try {
      const response = await apiInstance.post(endpoints.edit, {
        id: permissao?.id,
        codigoPermissao: permissao?.codigoPermissao,
        descricao: permissao?.descricao,
        color: permissao?.color,
        routesPermissao: routesParaSalvar(),
      });
      setAllPermissoes(response.data);

      setSnackbar({
        children: "Permissão editada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        children:
          error?.response?.data?.error || "Error: Falha ao editar permissão",
        severity: "error",
      });
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  const handleSalvar = () => {
    if (isEdit) {
      editPermissao();
    } else {
      createPermissao();
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => closeModal()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={styles.containerModal}>
        <Box sx={styles.BoxExitModal}>
          <IconButton>
            <ClearOutlined sx={{ fontSize: "20px" }} onClick={closeModal} />
          </IconButton>
        </Box>
        <Box>
          <Typography sx={styles.title}>
            {" "}
            {isEdit ? "Editar Permissão" : "Criar nova permissão"}{" "}
          </Typography>
        </Box>
        <Paper variant="outlined" sx={styles.paperBody}>
          <Box sx={styles.containerBox}>
            <Box sx={styles.boxBody}>
              <Box sx={styles.containerBody}>
                <Box sx={styles.boxPermissao}>
                  <Typography sx={styles.titlePermissao}>Permissão:</Typography>
                  <Chip
                    label={permissao.codigoPermissao || "NOVA PERMISSÃO"}
                    sx={{
                      ...styles.chip,
                      backgroundColor: permissao?.color,
                    }}
                  />
                </Box>
                <Box sx={styles.containerNomePermissao}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      disabled={permissaoObject?.codigoPermissao === permissaoProtegida}
                      label="Nome da Permissão"
                      size="small"
                      fullWidth // Faz o input ocupar 100% do container flex: 1
                      value={permissao?.codigoPermissao}
                      onChange={(e) =>
                        setPermissao((prev) => ({
                          ...prev,
                          codigoPermissao: e.target.value?.toUpperCase(),
                        }))
                      }
                      error={error && !permissao?.codigoPermissao?.trim()}
                      helperText={
                        error &&
                        !permissao?.codigoPermissao?.trim() &&
                        "O nome da permissão é obrigatório"
                      }
                    />
                  </Box>

                  <Box sx={{ flexShrink: 0 }}>
                    <Typography variant="caption" sx={styles.titleTag}>
                      Cor da TAG:
                    </Typography>

                    <Box sx={styles.containerColors}>
                      {colors.map((color) => {
                        const isSelected = permissao?.color === color;

                        return (
                          <IconButton
                            onClick={() =>
                              setPermissao((prev) => ({
                                ...prev,
                                color: color,
                              }))
                            }
                            sx={{
                              ...styles.iconButtonColor,

                              border: isSelected
                                ? `2px solid`
                                : "2px solid transparent",
                              borderColor: isSelected ? color : "transparent",
                            }}
                          >
                            <Box
                              sx={{
                                width: isSelected ? 14 : 18,
                                height: isSelected ? 14 : 18,
                                backgroundColor: color,
                                borderRadius: "50%",
                                transition: "all 0.2s ease-in-out",
                              }}
                            />
                          </IconButton>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box>
              <TextField
                value={permissao?.descricao}
                onChange={(e) =>
                  setPermissao((prev) => ({
                    ...prev,
                    descricao: e.target.value,
                  }))
                }
                label="Descrição"
                fullWidth
                multiline
                rows={4}
              />
            </Box>
            <MenuAcessos
              data={routesVisiveis}
              setRoutesSelect={setRoutesSelect}
              routesSelect={routesSelect}
              iconMap={iconMap}
            />
          </Box>
        </Paper>
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}
        >
          <Button
            disabled={loading}
            onClick={closeModal}
            variant="outlined"
            size="small"
          >
            Cancelar
          </Button>
          <Button
            disabled={loading}
            onClick={handleSalvar}
            variant="contained"
            size="small"
          >
            Salvar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
